import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import SystemIcon from "./SystemIcon";

const ToastContext = createContext(null);
let nextToastId = 1;

const DEFAULT_DURATION = {
  success: 4000,
  information: 4000,
  warning: 6000,
  danger: 7000,
};

const TONE_LABEL = {
  success: "완료",
  information: "안내",
  warning: "주의",
  danger: "오류",
};

function ToastItem({ toast, onDismiss }) {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || toast.duration <= 0) return undefined;
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [onDismiss, paused, toast.duration, toast.id]);

  const urgent = toast.tone === "danger";

  return (
    <section
      className={`ds-toast ds-toast--${toast.tone}`}
      role={urgent ? "alert" : "status"}
      aria-atomic="true"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <span className="ds-toast__indicator" aria-hidden="true" />
      <div className="ds-toast__content">
        <strong>{toast.title || TONE_LABEL[toast.tone]}</strong>
        <p>{toast.message}</p>
      </div>
      {toast.actionLabel && toast.onAction ? (
        <button
          type="button"
          className="ds-toast__action"
          onClick={() => {
            toast.onAction();
            onDismiss(toast.id);
          }}
        >
          {toast.actionLabel}
        </button>
      ) : null}
      <button
        type="button"
        className="ds-toast__close"
        onClick={() => onDismiss(toast.id)}
        aria-label="알림 닫기"
        title="알림 닫기"
      >
        <SystemIcon name="close" size="small" />
      </button>
    </section>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((tone, message, options = {}) => {
    const normalizedTone = DEFAULT_DURATION[tone] ? tone : "information";
    const normalizedMessage = String(message || "").trim();
    if (!normalizedMessage) return null;

    const toast = {
      id: nextToastId,
      tone: normalizedTone,
      message: normalizedMessage,
      title: options.title,
      duration: options.duration ?? DEFAULT_DURATION[normalizedTone],
      actionLabel: options.actionLabel,
      onAction: options.onAction,
    };
    nextToastId += 1;

    setToasts((current) => {
      const withoutDuplicate = current.filter((item) => (
        item.tone !== toast.tone || item.message !== toast.message
      ));
      return [...withoutDuplicate, toast].slice(-3);
    });

    return toast.id;
  }, []);

  const value = useMemo(() => ({
    show,
    dismiss,
    success: (message, options) => show("success", message, options),
    information: (message, options) => show("information", message, options),
    warning: (message, options) => show("warning", message, options),
    danger: (message, options) => show("danger", message, options),
    error: (message, options) => show("danger", message, options),
  }), [dismiss, show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="ds-toast-viewport" aria-label="알림">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
}
